import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { auth, db, storage } from '../firebase';
import {
  addDoc,
  collection,
  doc,
  setDoc,
  serverTimestamp,
  getDocs,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { QRCodeCanvas } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';

function AdminDashboard() {
  const navigate = useNavigate();
  const [eventInfo, setEventInfo] = useState({
    title: '',
    fields: [{ value: '' }],
    bannerUrl: '',
  });
  const [coupleEmail, setCoupleEmail] = useState('');
  const [couplePassword, setCouplePassword] = useState('');
  const [events, setEvents] = useState([]);
  const [editingEventId, setEditingEventId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [bannerUploaded, setBannerUploaded] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [saveEventMessage, setSaveEventMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  const fetchEvents = useCallback(async () => {
    setIsLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'events'));
      const eventsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        fields: Array.isArray(doc.data().fields) ? doc.data().fields : [{ value: doc.data().fields || '' }],
      }));
      eventsList.sort((a, b) => {
        const aTime = (a.updatedAt || a.createdAt)?.toMillis?.() || a.id;
        const bTime = (b.updatedAt || b.createdAt)?.toMillis?.() || b.id;
        return bTime - aTime;
      });
      setEvents(eventsList);
    } catch (err) {
      console.error('Error fetching events:', err);
      setSaveEventMessage('Failed to fetch events: ' + err.message);
      setTimeout(() => setSaveEventMessage(''), 3000);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (showModal || deleteConfirm) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      modalRef.current?.focus();
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      if (bannerPreview) URL.revokeObjectURL(bannerPreview);
    };
  }, [showModal, deleteConfirm, bannerPreview]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setUploadError('Please upload an image.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setUploadError('Image must be under 5MB.');
        return;
      }
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
      setUploadError('');
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    handleFileChange({ target: { files: [e.dataTransfer.files[0]] } });
  };

  const handleFileButtonClick = () => fileInputRef.current.click();

  const handleFieldChange = (e) => {
    const text = e.target.value;
    setEventInfo((prev) => ({
      ...prev,
      fields: [{ value: text }],
    }));
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleDownloadQR = (eventId, eventTitle, eventUrl) => {
    // Create a temporary off-screen container
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    document.body.appendChild(tempContainer);

    // Define the high-resolution QR code
    const tempCanvasId = `qr-code-high-res-${eventId}`;
    const tempQRCode = (
      <QRCodeCanvas
        id={tempCanvasId}
        value={eventUrl}
        size={512}
        level="H"
        includeMargin={true}
      />
    );

    // Render the high-resolution QR code
    const root = createRoot(tempContainer);
    root.render(tempQRCode);

    // Wait briefly for rendering to complete
    setTimeout(() => {
      const canvas = document.getElementById(tempCanvasId);
      if (canvas) {
        try {
          const link = document.createElement('a');
          link.download = `${eventTitle || 'event'}-qr-code.png`;
          link.href = canvas.toDataURL('image/png', 1.0);
          link.click();
        } catch (err) {
          console.error('Download failed:', err);
          setSaveEventMessage('Failed to download QR code.');
          setTimeout(() => setSaveEventMessage(''), 3000);
        }
      } else {
        console.error('Canvas not found:', tempCanvasId);
        setSaveEventMessage('Failed to generate QR code.');
        setTimeout(() => setSaveEventMessage(''), 3000);
      }

      // Clean up
      root.unmount();
      document.body.removeChild(tempContainer);
    }, 100); // Small delay to ensure canvas rendering
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!eventInfo.title || !eventInfo.fields[0].value) {
      setSaveEventMessage('Please fill all required event fields.');
      setTimeout(() => setSaveEventMessage(''), 3000);
      return;
    }
    if (!editingEventId && (!coupleEmail || !couplePassword)) {
      setSaveEventMessage('Please provide couple email and password.');
      setTimeout(() => setSaveEventMessage(''), 3000);
      return;
    }
    if (!editingEventId && !validateEmail(coupleEmail.trim())) {
      setSaveEventMessage('Please provide a valid email address.');
      setTimeout(() => setSaveEventMessage(''), 3000);
      return;
    }
    if (!editingEventId && couplePassword.length < 6) {
      setSaveEventMessage('Password must be at least 6 characters.');
      setTimeout(() => setSaveEventMessage(''), 3000);
      return;
    }
    setIsLoading(true);
    try {
      let bannerUrl = eventInfo.bannerUrl;
      if (bannerFile) {
        const storageRef = ref(storage, `banners/${editingEventId || `new_${Date.now()}`}/${bannerFile.name}`);
        await uploadBytes(storageRef, bannerFile);
        bannerUrl = await getDownloadURL(storageRef);
        setBannerFile(null);
        setBannerPreview(null);
        setBannerUploaded(true);
        setTimeout(() => setBannerUploaded(false), 2000);
        fileInputRef.current.value = '';
      }
      if (editingEventId) {
        const updates = {
          title: eventInfo.title,
          fields: eventInfo.fields,
          bannerUrl: bannerUrl || '',
          updatedAt: serverTimestamp(),
        };
        await updateDoc(doc(db, 'events', editingEventId), updates);
        setSaveEventMessage('Event updated!');
      } else {
        const eventRef = await addDoc(collection(db, 'events'), {
          title: eventInfo.title,
          fields: eventInfo.fields,
          bannerUrl: bannerUrl || '',
          createdAt: serverTimestamp(),
        });
        try {
          const userCredential = await createUserWithEmailAndPassword(auth, coupleEmail.trim(), couplePassword);
          console.log('Created user UID:', userCredential.user.uid);
          await setDoc(doc(db, 'users', userCredential.user.uid), {
            email: coupleEmail.trim(),
            role: 'couple',
            eventId: eventRef.id,
            createdAt: serverTimestamp(),
          });
          setSaveEventMessage('Event and account created!');
        } catch (authError) {
          console.error('Auth error:', authError.code, authError.message);
          let errorMessage = 'Failed to create account: ' + authError.message;
          if (authError.code === 'auth/email-already-in-use') {
            errorMessage = 'Error: Email already in use.';
          } else if (authError.code === 'auth/invalid-email') {
            errorMessage = 'Error: Invalid email format.';
          } else if (authError.code === 'auth/weak-password') {
            errorMessage = 'Error: Password is too weak.';
          }
          await deleteDoc(doc(db, 'events', eventRef.id));
          setSaveEventMessage(errorMessage);
          setTimeout(() => setSaveEventMessage(''), 3000);
          setIsLoading(false);
          return;
        }
      }
      setTimeout(() => setSaveEventMessage(''), 2000);
      resetForm();
      fetchEvents();
    } catch (err) {
      console.error('Error:', err.code, err.message);
      setSaveEventMessage('Failed to save: ' + err.message);
      setTimeout(() => setSaveEventMessage(''), 3000);
    }
    setIsLoading(false);
  };

  const resetForm = () => {
    setEventInfo({ title: '', fields: [{ value: '' }], bannerUrl: '' });
    setCoupleEmail('');
    setCouplePassword('');
    setBannerFile(null);
    setBannerPreview(null);
    setUploadError('');
    setEditingEventId(null);
    setShowModal(false);
    setDeleteConfirm(null);
  };

  const handleEdit = async (event) => {
    setEditingEventId(event.id);
    setEventInfo({
      title: event.title || '',
      fields: [{ value: event.fields[0]?.value || '' }],
      bannerUrl: event.bannerUrl || '',
    });
    setCoupleEmail('');
    setCouplePassword('');
    setBannerFile(null);
    setBannerPreview(null);
    setUploadError('');
    setShowModal(true);
  };

  const handleDelete = async (eventId) => {
    setIsLoading(true);
    try {
      await deleteDoc(doc(db, 'events', eventId));
      const userQuery = await getDocs(collection(db, 'users'));
      const user = userQuery.docs.find((doc) => doc.data().eventId === eventId);
      if (user) await deleteDoc(doc(db, 'users', user.id));
      setSaveEventMessage('Event deleted!');
      fetchEvents();
      setDeleteConfirm(null);
    } catch (err) {
      setSaveEventMessage('Delete failed: ' + err.message);
    }
    setTimeout(() => setSaveEventMessage(''), 2000);
    setIsLoading(false);
  };

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      await signOut(auth);
      navigate('/');
    } catch (err) {
      setSaveEventMessage('Logout failed: ' + err.message);
      setTimeout(() => setSaveEventMessage(''), 3000);
    }
    setIsLoading(false);
  };

  return (
    <div className="admin-page">
      <div className="admin-container">
        <img src="/logo.png" alt="Logo" className="admin-logo" />
        <div className="admin-header">
          <h1 className="admin-heading">Admin Dashboard</h1>
          <div className="header-buttons">
            <button className="create-btn" onClick={() => setShowModal(true)} disabled={isLoading}>
              New Event
            </button>
            <button className="logout-btn" onClick={handleLogout} disabled={isLoading}>
              {isLoading ? 'Logging out...' : 'Logout'}
            </button>
          </div>
        </div>
        {saveEventMessage && (
          <div className={`message ${saveEventMessage.includes('Failed') || saveEventMessage.includes('Error') ? 'error' : 'success'}`}>
            {saveEventMessage}
          </div>
        )}
        {showModal && (
          <div className="modal" role="dialog" aria-labelledby="modal-title" ref={modalRef} tabIndex="-1">
            <div className="modal-content">
              <h2 id="modal-title" className="section-heading">
                {editingEventId ? 'Edit Event' : 'New Event'}
              </h2>
              <form onSubmit={handleSubmit} className="form-wrapper">
                <div className="form-section">
                  <div className="form-group">
                    <label htmlFor="event-title" className="admin-label">
                      Event Title <span className="required">*</span>
                    </label>
                    <input
                      id="event-title"
                      type="text"
                      placeholder="e.g., Wedding Day 2025"
                      value={eventInfo.title}
                      onChange={(e) => setEventInfo((p) => ({ ...p, title: e.target.value }))}
                      required
                      className="admin-input"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="event-details" className="admin-label">
                      Event Details <span className="required">*</span><br />
                      <small>(Enter all details freely, spaces and line breaks preserved)</small>
                    </label>
                    <textarea
                      id="event-details"
                      placeholder="e.g., Grand Ballroom, Cocktail Hour"
                      value={eventInfo.fields[0].value}
                      onChange={handleFieldChange}
                      required
                      className="admin-textarea"
                      rows="5"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="banner-upload" className="admin-label">
                      Banner Image (Optional)
                    </label>
                    <div className="drop-zone" onDragOver={(e) => e.preventDefault()} onDrop={handleFileDrop}>
                      <p>Drag an image here or</p>
                      <button type="button" className="file-upload-btn" onClick={handleFileButtonClick}>
                        Choose File
                      </button>
                      <input
                        id="banner-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                        className="file-input-hidden"
                      />
                    </div>
                    {(bannerPreview || eventInfo.bannerUrl) && (
                      <div className="banner-preview-container">
                        {bannerPreview && (
                          <div className="banner-preview">
                            <p>Preview:</p>
                            <img src={bannerPreview} alt="Banner preview" />
                          </div>
                        )}
                        {eventInfo.bannerUrl && (
                          <div className="banner-preview">
                            <p>Current:</p>
                            <img src={eventInfo.bannerUrl} alt="Current banner" />
                          </div>
                        )}
                      </div>
                    )}
                    {uploadError && <span className="error-message">{uploadError}</span>}
                    {bannerUploaded && <span className="success-message">Uploaded!</span>}
                  </div>
                </div>
                {!editingEventId && (
                  <div className="form-section">
                    <h3 className="section-heading">Couple Account</h3>
                    <div className="form-group">
                      <label htmlFor="couple-email" className="admin-label">
                        Email <span className="required">*</span>
                      </label>
                      <input
                        id="couple-email"
                        type="email"
                        placeholder="e.g., couple@example.com"
                        value={coupleEmail}
                        onChange={(e) => setCoupleEmail(e.target.value.trim())}
                        required
                        className="admin-input"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="couple-password" className="admin-label">
                        Password <span className="required">*</span>
                      </label>
                      <input
                        id="couple-password"
                        type="password"
                        placeholder="Min 6 characters"
                        value={couplePassword}
                        onChange={(e) => setCouplePassword(e.target.value)}
                        required
                        minLength={6}
                        className="admin-input"
                      />
                    </div>
                  </div>
                )}
                <div className="form-buttons">
                  <button type="submit" className="submit-btn" disabled={isLoading}>
                    {isLoading ? 'Saving...' : editingEventId ? 'Update' : 'Create'}
                  </button>
                  <button type="button" className="cancel-btn" onClick={resetForm} disabled={isLoading}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {deleteConfirm && (
          <div className="modal" role="dialog" aria-labelledby="delete-confirm-title" tabIndex="-1">
            <div className="modal-content">
              <h2 id="delete-confirm-title" className="section-heading">Confirm Delete</h2>
              <p>Are you sure you want to delete this event? This action cannot be undone.</p>
              <div className="form-buttons">
                <button
                  className="submit-btn"
                  onClick={() => handleDelete(deleteConfirm)}
                  disabled={isLoading}
                  aria-label="Confirm delete event"
                >
                  {isLoading ? 'Deleting...' : 'Yes, Delete'}
                </button>
                <button
                  className="cancel-btn"
                  onClick={() => setDeleteConfirm(null)}
                  disabled={isLoading}
                  aria-label="Cancel delete event"
                >
                  No, Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="events-section">
          <h2 className="section-heading">Events</h2>
          {isLoading && <div className="loading">Loading...</div>}
          {events.length === 0 && !isLoading && <p className="no-events">No events yet.</p>}
          <ul className="events-list">
            {events.map((event) => {
              const eventUrl = `${window.location.origin}/rsvp/${event.id}`;
              return (
                <li key={event.id} className="event-item">
                  <button
                    className="delete-btn"
                    onClick={() => setDeleteConfirm(event.id)}
                    disabled={isLoading}
                    aria-label={`Delete event ${event.title || 'Untitled Event'}`}
                  >
                    ✕
                  </button>
                  <div className="event-details">
                    <h3 className="event-title">{event.title || 'Untitled Event'}</h3>
                    <p className="event-field">{event.fields[0]?.value || 'No details available'}</p>
                    {event.bannerUrl && (
                      <img src={event.bannerUrl} alt={`Banner for ${event.title}`} className="event-banner" />
                    )}
                    <QRCodeCanvas
                      id={`qr-code-${event.id}`}
                      value={eventUrl}
                      size={100}
                      level="H"
                      includeMargin={true}
                      className="event-qr"
                    />
                    <div className="event-actions">
                      <a href={eventUrl} target="_blank" rel="noopener noreferrer" className="view-link">
                        View
                      </a>
                      <button className="edit-btn" onClick={() => handleEdit(event)} disabled={isLoading}>
                        Edit
                      </button>
                      <button
                        className="download-qr-btn"
                        onClick={() => handleDownloadQR(event.id, event.title, eventUrl)}
                        disabled={isLoading}
                        aria-label={`Download QR code for ${event.title || 'Untitled Event'}`}
                      >
                        Download QR
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        <footer className="admin-footer">
          <p className="footer-text">
            By <a href="https://get-gs.com" target="_blank" rel="noopener noreferrer" className="footer-link">GS</a>
          </p>
        </footer>
      </div>
    </div>
  );
}

export default AdminDashboard;
